import React from "react";

const formatDateTime = (value) => {
  if (!value) return "-";

  try {
    const date = new Date(value);
    const datePart = new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
    const timePart = new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);

    return `${datePart} ${timePart}`;
  } catch {
    return String(value);
  }
};

const formatDateOnly = (value) => {
  if (!value) return "-";

  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
};

const toText = (value, fallback = "-") => {
  const text = String(value ?? "").trim();
  return text || fallback;
};

export function AllTasksReportPdfDocument({ components, companyInfo, filters, report, selectedColumns, generatedAt }) {
  const { Document, Page, StyleSheet, Text, View } = components;
  const activeColumns = Array.isArray(selectedColumns) && selectedColumns.length
    ? selectedColumns
    : ["department", "employee", "task", "priority", "status", "activity", "actions"];
  const columnWidth = `${100 / activeColumns.length}%`;

  const styles = StyleSheet.create({
    page: {
      padding: 22,
      backgroundColor: "#f8fafc",
      fontSize: 8.6,
      color: "#0f172a",
    },
    header: {
      backgroundColor: "#0f172a",
      borderRadius: 12,
      padding: 14,
      marginBottom: 10,
    },
    title: {
      color: "#ffffff",
      fontSize: 16,
      fontWeight: 700,
      marginBottom: 4,
    },
    subtitle: {
      color: "#cbd5e1",
      fontSize: 8.5,
      lineHeight: 1.5,
    },
    summaryRow: {
      flexDirection: "row",
      marginBottom: 10,
    },
    summaryCard: {
      flexGrow: 1,
      flexBasis: 0,
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#e2e8f0",
      borderRadius: 10,
      padding: 9,
      marginRight: 8,
    },
    summaryCardLast: {
      marginRight: 0,
    },
    summaryLabel: {
      fontSize: 7.3,
      color: "#64748b",
      textTransform: "uppercase",
      marginBottom: 2,
    },
    summaryValue: {
      fontSize: 12.5,
      fontWeight: 700,
      color: "#0f172a",
      marginBottom: 2,
    },
    summaryHelper: {
      fontSize: 7.5,
      color: "#475569",
    },
    sectionTitle: {
      fontSize: 10.5,
      fontWeight: 700,
      marginBottom: 4,
    },
    filterBox: {
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#cbd5e1",
      borderRadius: 8,
      paddingVertical: 7,
      paddingHorizontal: 9,
      marginBottom: 8,
    },
    filterText: {
      fontSize: 8.2,
      lineHeight: 1.45,
      color: "#0f172a",
    },
    table: {
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#cbd5e1",
      borderRadius: 10,
      overflow: "hidden",
    },
    tableHeader: {
      flexDirection: "row",
      backgroundColor: "#0f172a",
      paddingVertical: 7,
    },
    headerText: {
      color: "#e2e8f0",
      fontSize: 6.8,
      fontWeight: 700,
      textTransform: "uppercase",
      paddingHorizontal: 5,
    },
    row: {
      flexDirection: "row",
      paddingVertical: 6,
      borderBottomWidth: 1,
      borderBottomColor: "#e2e8f0",
    },
    rowAlt: {
      backgroundColor: "#f8fafc",
    },
    rowLast: {
      borderBottomWidth: 0,
    },
    cell: {
      paddingHorizontal: 5,
    },
    cellStrong: {
      fontSize: 7.3,
      fontWeight: 700,
      lineHeight: 1.35,
    },
    cellText: {
      fontSize: 7.1,
      color: "#334155",
      lineHeight: 1.35,
      marginTop: 1,
    },
    emptyState: {
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#cbd5e1",
      borderRadius: 8,
      padding: 14,
    },
    emptyStateText: {
      fontSize: 8.5,
      color: "#475569",
      textAlign: "center",
      lineHeight: 1.5,
    },
    footer: {
      marginTop: 8,
      paddingTop: 6,
      borderTopWidth: 1,
      borderTopColor: "#cbd5e1",
      flexDirection: "row",
      justifyContent: "space-between",
    },
    footerText: {
      fontSize: 7.6,
      color: "#64748b",
    },
  });

  const statusCounts = Array.isArray(report?.statusCounts) ? report.statusCounts : [];
  const priorityCounts = Array.isArray(report?.priorityCounts) ? report.priorityCounts : [];
  const completedCount = statusCounts.find((item) => item.status === "Completed")?.count || 0;
  const inProgressCount = statusCounts.find((item) => item.status === "In Progress")?.count || 0;
  const urgentCount = priorityCounts.find((item) => item.priority === "Urgent")?.count || 0;

  const filterSummary = [
    `Department: ${toText(filters?.departmentName, "All Departments")}`,
    `Employee: ${toText(filters?.employeeName, "All Employees")}`,
    `Date: ${formatDateOnly(filters?.startDate)} to ${formatDateOnly(filters?.endDate)}`,
    `Status: ${toText(filters?.status, "All Statuses")}`,
    `Priority: ${toText(filters?.priority, "All Priorities")}`,
    `Generated: ${formatDateTime(generatedAt)}`,
  ].join(" | ");

  const columns = [
    {
      key: "department",
      header: "Department",
      render: (item) => (
        <View style={[styles.cell, { width: columnWidth }]}>
          <Text style={styles.cellStrong}>{toText(item.departmentName, "No Department")}</Text>
        </View>
      ),
    },
    {
      key: "employee",
      header: "Employee",
      render: (item) => (
        <View style={[styles.cell, { width: columnWidth }]}>
          <Text style={styles.cellStrong}>{toText(item.assignedEmployeeName, "Unassigned")}</Text>
          <Text style={styles.cellText}>{toText(item.assignedEmployeeEmail || item.assignedUserId, "No email")}</Text>
        </View>
      ),
    },
    {
      key: "task",
      header: "Task",
      render: (item) => (
        <View style={[styles.cell, { width: columnWidth }]}>
          <Text style={styles.cellStrong}>{toText(item.title, "Untitled task")}</Text>
          <Text style={styles.cellText}>{toText(item.description, "No description")}</Text>
          <Text style={styles.cellText}>{toText(item.ticketNumber, "Manual task")} | {toText(item.ticketSubject, "No ticket subject")}</Text>
        </View>
      ),
    },
    {
      key: "priority",
      header: "Priority",
      render: (item) => (
        <View style={[styles.cell, { width: columnWidth }]}>
          <Text style={styles.cellStrong}>{toText(item.priority, "Low")}</Text>
        </View>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => (
        <View style={[styles.cell, { width: columnWidth }]}>
          <Text style={styles.cellStrong}>{toText(item.status, "Pending")}</Text>
          <Text style={styles.cellText}>Progress {Number(item.progressPercent || 0)}%</Text>
        </View>
      ),
    },
    {
      key: "activity",
      header: "Last Activity",
      render: (item) => (
        <View style={[styles.cell, { width: columnWidth }]}>
          <Text style={styles.cellStrong}>{formatDateTime(item.lastActivityAt || item.updatedAt)}</Text>
          <Text style={styles.cellText}>{toText(item.lastActionLabel, "Task Updated")} by {toText(item.lastActorName, "Unknown")}</Text>
          <Text style={styles.cellText}>Due {formatDateOnly(item.dueDate)}</Text>
        </View>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (item) => (
        <View style={[styles.cell, { width: columnWidth }]}>
          <Text style={styles.cellStrong}>{item.ticketId ? "Ticket / Task Desk" : "Task Desk"}</Text>
          <Text style={styles.cellText}>{item.ticketId ? `Ticket ${toText(item.ticketNumber, "Open Ticket")}` : "Manual task"}</Text>
        </View>
      ),
    },
  ].filter((column) => activeColumns.includes(column.key));

  return (
    <Document title="All Task Report" author="BanglaFire CRM">
      <Page size="A4" orientation="landscape" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>{toText(companyInfo?.name, "BanglaFire CRM")} All Task Report</Text>
          <Text style={styles.subtitle}>
            {toText(companyInfo?.address, "BanglaFire CRM Support Desk")} | {toText(companyInfo?.email, "support@banglafire.com")}
          </Text>
        </View>

        <View style={styles.summaryRow}>
          {[
            { label: "Total Tasks", value: String(report?.totalTasks || 0), helper: `${report?.items?.length || 0} rows in this export` },
            { label: "Completed", value: String(completedCount), helper: `In Progress ${inProgressCount}` },
            { label: "Urgent", value: String(urgentCount), helper: `Priority-based task pressure` },
          ].map((item, index, array) => (
            <View key={item.label} style={[styles.summaryCard, index === array.length - 1 ? styles.summaryCardLast : null]}>
              <Text style={styles.summaryLabel}>{item.label}</Text>
              <Text style={styles.summaryValue}>{item.value}</Text>
              <Text style={styles.summaryHelper}>{item.helper}</Text>
            </View>
          ))}
        </View>

        <Text style={styles.sectionTitle}>Report Filters</Text>
        <View style={styles.filterBox}>
          <Text style={styles.filterText}>{filterSummary}</Text>
        </View>

        <Text style={styles.sectionTitle}>Task Rows</Text>
        {report?.items?.length ? (
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              {columns.map((column) => (
                <Text key={column.key} style={[styles.headerText, { width: columnWidth }]}>
                  {column.header}
                </Text>
              ))}
            </View>
            {report.items.map((item, index) => (
              <View
                key={item.taskId || `${item.title}-${index}`}
                style={[styles.row, index % 2 === 1 ? styles.rowAlt : null, index === report.items.length - 1 ? styles.rowLast : null]}
                wrap={false}
              >
                {columns.map((column) => (
                  <React.Fragment key={column.key}>
                    {column.render(item)}
                  </React.Fragment>
                ))}
              </View>
            ))}
          </View>
        ) : (
          <View style={styles.emptyState}>
            <Text style={styles.emptyStateText}>No tasks matched the selected filters for this report.</Text>
          </View>
        )}

        <View style={styles.footer}>
          <Text style={styles.footerText}>Generated on {formatDateTime(generatedAt)}</Text>
          <Text style={styles.footerText}>BanglaFire CRM Professional Report</Text>
        </View>
      </Page>
    </Document>
  );
}
